import { useState, useEffect } from 'react';

import toast from 'react-hot-toast';
import { Loader2, UserPlus, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useResponsive } from '@/hooks/useResponsive';
import { useFeatures } from '@/hooks/useFeatures';
import { useWorkspace } from '@/contexts/workspace-context';
import { useEmbedStore } from '@/stores/embed-store';
import { cn } from '@/lib/utils';

import { MemberRow } from './member-row';
import { OwnerCard } from './owner-card';
import { PendingInvitations } from './pending-invitations';
import { InviteMemberDialog } from './invite-member-dialog';
import { workspaceMembersApi } from '@/lib/api/workspace-members.api';
import type {
  MembersListResponse,
  WorkspaceMemberResponse,
  WorkspaceRole,
} from '@optidev/shared-types';

interface GroupLabelProps {
  name: string;
  count?: number;
  hint?: string;
}

/** Uppercase section label above a member group (Owner / Admins / Members / Viewers). */
function GroupLabel({ name, count, hint }: GroupLabelProps) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[10.5px] font-semibold uppercase tracking-widest text-muted-foreground">
        {name}
      </span>
      {count !== undefined && (
        <span className="text-[11px] tabular-nums text-muted-foreground/70">
          {count}
        </span>
      )}
      {hint && (
        <span className="ml-auto text-[11px] text-muted-foreground/80">
          {hint}
        </span>
      )}
    </div>
  );
}

/**
 * Members List Component
 * Main component for displaying workspace members and pending invitations
 */
export function MembersList() {
  const [data, setData] = useState<MembersListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const { isMobile } = useResponsive();
  const { features } = useFeatures();
  const { activeWorkspace } = useWorkspace();
  const isEmbedded = useEmbedStore((s) => s.isEmbedded);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const result = await workspaceMembersApi.getMembers();
      setData(result);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const canInvite =
    data?.currentUserRole === 'owner' || data?.currentUserRole === 'admin';

  const workspaceName = activeWorkspace?.name ?? '';
  // Ownership transfer: feature-flagged, never for embed workspaces (managed
  // by the OptiSigns account); per-member visibility rules live in MemberRow.
  const canTransferOwnership =
    features.ownershipTransfer && !isEmbedded && workspaceName.length > 0;

  const handleRoleChange = async (memberId: string, newRole: WorkspaceRole) => {
    try {
      await workspaceMembersApi.updateMemberRole(memberId, newRole);
      toast.success('Role updated');
      fetchMembers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      await workspaceMembersApi.removeMember(memberId);
      toast.success('Member removed');
      fetchMembers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to remove member');
    }
  };

  const handleLeave = async () => {
    try {
      await workspaceMembersApi.leaveWorkspace();
      toast.success('You have left the workspace');
      // Redirect to dashboard - will auto-switch to another workspace
      window.location.href = '/dashboard';
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to leave workspace');
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      await workspaceMembersApi.revokeInvitation(invitationId);
      toast.success('Invitation revoked');
      fetchMembers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to revoke invitation');
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await workspaceMembersApi.resendInvitation(invitationId);
      toast.success('Invitation resent');
      fetchMembers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to resend invitation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data || !data.members) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Failed to load members
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col gap-4 pb-5 border-b border-border/50">
        <div className={cn(
          "flex gap-4",
          isMobile ? "flex-col" : "items-center justify-between"
        )}>
          <div>
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Workspace Members</h2>
            <p className="text-xs text-muted-foreground mt-1">
              {data.totalCount} member{data.totalCount !== 1 ? 's' : ''}
              {data.memberLimit && ` of ${data.memberLimit} allowed`}
            </p>
          </div>
          {canInvite && (
            <Button
              variant="ghost"
              onClick={() => setInviteDialogOpen(true)}
              className={cn("text-xs h-8 px-3 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground", isMobile && "w-full")}
            >
              Invite Member
            </Button>
          )}
        </div>
      </div>

      {/* Owner + role groups */}
      {(() => {
        const owner = data.members.find((m) => m.isOwner);
        const groups: Array<{
          name: string;
          hint?: string;
          members: WorkspaceMemberResponse[];
        }> = [
          {
            name: 'Admins',
            hint: 'Can receive ownership',
            members: data.members.filter((m) => !m.isOwner && m.role === 'admin'),
          },
          {
            name: 'Members',
            members: data.members.filter((m) => m.role === 'user'),
          },
          {
            name: 'Viewers',
            members: data.members.filter((m) => m.role === 'viewer'),
          },
        ];

        const renderRow = (member: WorkspaceMemberResponse) => (
          <MemberRow
            key={member.id}
            member={member}
            currentUserRole={data.currentUserRole}
            isCurrentUserOwner={data.isCurrentUserOwner}
            workspaceName={workspaceName}
            canTransferOwnership={canTransferOwnership}
            onRoleChange={handleRoleChange}
            onRemove={handleRemoveMember}
            onLeave={handleLeave}
            onTransferred={fetchMembers}
          />
        );

        return (
          <div className="space-y-5">
            {owner && (
              <div className="space-y-2">
                <GroupLabel name="Owner" hint="Billing & workspace deletion" />
                <OwnerCard member={owner} />
              </div>
            )}
            {groups
              .filter((group) => group.members.length > 0)
              .map((group) => (
                <div key={group.name} className="space-y-2">
                  <GroupLabel
                    name={group.name}
                    count={group.members.length}
                    hint={group.hint}
                  />
                  <div className="rounded-xl border border-border/70 px-4 divide-y divide-border/50">
                    {group.members.map(renderRow)}
                  </div>
                </div>
              ))}
          </div>
        );
      })()}

      {/* Pending Invitations */}
      {canInvite && data.pendingInvitations.length > 0 && (
        <PendingInvitations
          invitations={data.pendingInvitations}
          onRevoke={handleRevokeInvitation}
          onResend={handleResendInvitation}
        />
      )}

      {/* Invite Dialog */}
      <InviteMemberDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        onSuccess={fetchMembers}
        memberLimit={data.memberLimit}
        currentCount={data.totalCount}
      />
    </div>
  );
}
